/**
 * Password input with show/hide toggle.
 *
 * 44px minimum height, WCAG AA contrast, proper label association.
 */
import { useCallback, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IconEye, IconEyeOff } from './icons.tsx';
import styles from './PasswordInput.module.css';

interface Props {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoFocus?: boolean;
  autoComplete?: string;
}

export function PasswordInput({
  label,
  value,
  onChange,
  autoFocus,
  autoComplete,
}: Props): React.JSX.Element {
  const id = useId();
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);

  const toggle = useCallback(() => setVisible((v) => !v), []);

  return (
    <div className={styles['input-group']}>
      <label htmlFor={id} className={`ns-label-caps ${styles['label']}`}>
        {label}
      </label>
      <div className={styles['wrapper']}>
        <input
          id={id}
          className={`ns-field ${styles['input']}`}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
        />
        <button
          type="button"
          className={visible ? `${styles['toggle']} ${styles['toggle-active']}` : styles['toggle']}
          onClick={toggle}
          aria-label={visible ? t('field.hidePassword') : t('field.showPassword')}
          title={visible ? t('field.hidePassword') : t('field.showPassword')}
        >
          {visible ? <IconEyeOff size={20} /> : <IconEye size={20} />}
        </button>
      </div>
    </div>
  );
}
